import { equals, evolve, filter, length, pipe, values } from 'ramda'

import { AmplifyGraphQLclient } from './amplify'

const query = /* GraphQL */ `
  query hasDuplicates($orgId: ID!, $name: String!) {
    getOrganization(id: $orgId) {
      customComponents(limit: 100, filter: { name: { eq: $name } }) {
        items {
          id
        }
        nextToken
      }
      engagementGoals(limit: 100, filter: { name: { eq: $name } }) {
        items {
          id
        }
        nextToken
      }
      solution(limit: 100, filter: { name: { eq: $name } }) {
        items {
          id
        }
        nextToken
      }
    }
  }
`

type QueryResult = {
  data: {
    getOrganization: {
      customComponents: {
        items: Array<{ id: string }>
        nextToken: string | null
      }
      engagementGoals: {
        items: Array<{ id: string }>
        nextToken: string | null
      }
      solution: {
        items: Array<{ id: string }>
        nextToken: string | null
      }
    }
  }
}
/**
 * This will check if there is no other solution/eg/component with the same name inside of the organization
 * @param name {string}
 * @param orgID {string}
 */
export async function canUseName(
  orgID: string | undefined,
  componentID: string | undefined,
  name: any
) {
  if (!orgID) {
    console.warn('orgID is not there')
    return false
  }

  if (typeof name !== 'string') {
    console.warn('name is not a string')
    return false
  }
  if (!name) return true
  const req = (await AmplifyGraphQLclient.graphql({
    query,
    variables: {
      name,
      orgId: orgID,
    },
  })) as QueryResult

  return hasItems(req, componentID) || 'The name is already used'
}

const hasItems = (data: QueryResult, componentID?: string) => {
  const hasItemUnderName = (
    data: QueryResult['data']['getOrganization']['customComponents']
  ) => {
    const { items } = data
    return (
      items.filter((i) => !componentID || (componentID && i.id !== componentID))
        .length > 0
    )
  }

  const canContinue = pipe(
    evolve({
      customComponents: hasItemUnderName,
      engagementGoals: hasItemUnderName,
      solution: hasItemUnderName,
    }),
    values,
    filter(Boolean),
    length,
    equals(0)
  )
  return canContinue(data.data.getOrganization)
}

if (import.meta.vitest) {
  const { describe, it, expect, vi, beforeEach, afterEach } = import.meta.vitest
  describe('hasItems', () => {
    it.each([
      [
        {
          data: {
            getOrganization: {
              customComponents: {
                items: [{ id: '1' }, { id: '2' }],
                nextToken: null,
              },
              engagementGoals: {
                items: [],
                nextToken: null,
              },
              solution: {
                items: [],
                nextToken: null,
              },
            },
          },
        },
        '1',
        false,
      ],
      [
        {
          data: {
            getOrganization: {
              customComponents: {
                items: [{ id: '1' }, { id: '2' }],
                nextToken: null,
              },
              engagementGoals: {
                items: [{ id: '11' }, { id: '2' }],
                nextToken: null,
              },
              solution: {
                items: [{ id: '1' }, { id: '2' }],
                nextToken: null,
              },
            },
          },
        },
        '1',
        false,
      ],
      [
        {
          data: {
            getOrganization: {
              customComponents: {
                items: [],
                nextToken: null,
              },
              engagementGoals: {
                items: [],
                nextToken: null,
              },
              solution: {
                items: [],
                nextToken: null,
              },
            },
          },
        },
        '1',
        true,
      ],
      [
        {
          data: {
            getOrganization: {
              customComponents: {
                items: [{ id: '1' }],
                nextToken: null,
              },
              engagementGoals: {
                items: [],
                nextToken: null,
              },
              solution: {
                items: [],
                nextToken: null,
              },
            },
          },
        },
        '1',
        true,
      ],
      [
        {
          data: {
            getOrganization: {
              customComponents: {
                items: [{ id: '1' }],
                nextToken: null,
              },
              engagementGoals: {
                items: [],
                nextToken: null,
              },
              solution: {
                items: [],
                nextToken: null,
              },
            },
          },
        },
        undefined,
        false,
      ],
    ])(
      'should check if there are components with the same name',
      (requestData, componentID, expectedValue) => {
        expect(hasItems(requestData, componentID)).toBe(expectedValue)
      }
    )
  })

  describe('canUseName', () => {
    const graphqlSpy = vi.spyOn(AmplifyGraphQLclient, 'graphql')
    const warnSpy = vi.spyOn(console, 'warn').mockImplementation(() => {})

    beforeEach(() => {
      graphqlSpy.mockReset()
      warnSpy.mockClear()
    })

    afterEach(() => {
      graphqlSpy.mockReset()
      warnSpy.mockClear()
    })

    it('returns false when orgID is missing', async () => {
      await expect(canUseName(undefined, undefined, 'name')).resolves.toBe(
        false
      )
      expect(warnSpy).toHaveBeenCalledWith('orgID is not there')
      expect(graphqlSpy).not.toHaveBeenCalled()
    })

    it('returns false when name is not a string', async () => {
      await expect(canUseName('org-1', undefined, 123)).resolves.toBe(false)
      expect(warnSpy).toHaveBeenCalledWith('name is not a string')
      expect(graphqlSpy).not.toHaveBeenCalled()
    })

    it('returns true for an empty name', async () => {
      await expect(canUseName('org-1', undefined, '')).resolves.toBe(true)
      expect(graphqlSpy).not.toHaveBeenCalled()
    })

    it('returns warning message when duplicates exist', async () => {
      graphqlSpy.mockResolvedValue({
        data: {
          getOrganization: {
            customComponents: { items: [{ id: '1' }], nextToken: null },
            engagementGoals: { items: [], nextToken: null },
            solution: { items: [], nextToken: null },
          },
        },
      })

      await expect(canUseName('org-1', undefined, 'name')).resolves.toBe(
        'The name is already used'
      )
      expect(graphqlSpy).toHaveBeenCalledTimes(1)
    })

    it('returns true when no duplicates exist', async () => {
      graphqlSpy.mockResolvedValue({
        data: {
          getOrganization: {
            customComponents: { items: [], nextToken: null },
            engagementGoals: { items: [], nextToken: null },
            solution: { items: [], nextToken: null },
          },
        },
      })

      await expect(canUseName('org-1', undefined, 'name')).resolves.toBe(true)
      expect(graphqlSpy).toHaveBeenCalledTimes(1)
    })
  })
}
